import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { Badge as MaterialBadge, BadgeProps } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React from 'react';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    badgeSmall: {
      borderRadius: '5px',
      height: '10px',
      minWidth: '10px',
      padding: 0,
    },
    badgeMedium: {
      borderRadius: '7.5px',
      height: '15px',
      minWidth: '15px',
      padding: 0,
    },
    badgeLarge: {
      borderRadius: '10px',
      height: '20px',
      minWidth: '20px',
      padding: 0,
    },
    error: {
      backgroundColor: theme.palette.red[400],
    },
    info: {
      backgroundColor: theme.palette.primary.main,
    },
    success: {
      backgroundColor: theme.palette.green[700],
    },
    warning: {
      backgroundColor: theme.palette.yellow[800],
    },
  })
);

interface Props extends BadgeProps {
  status?: 'ERROR' | 'INFO' | 'SUCCESS' | 'WARNING';
  size?: 'SMALL' | 'MEDIUM' | 'LARGE';
}

/**
 * This is a wrapper around the Material-UI badge with J1 specific styles.
 * All of the props for this component are the same as the Material-UI badge,
 * and are just passed to that component, with the exception of a few J1 specifics,
 * so check out this page for a lot of the prop declarations:
 * https://material-ui.com/components/badges/#badge
 */
const Badge = ({
  anchorOrigin = { vertical: 'top', horizontal: 'left' },
  badgeContent = ' ',
  children,
  classes: customClasses,
  invisible = false,
  overlap = 'rectangle',
  status,
  size = 'MEDIUM',
  ...otherProps
}: Props) => {
  const classes = useStyles();

  // Need to pull out the badge classes and merge them below
  const badgeClasses = customClasses?.badge;
  delete customClasses?.badge;

  return (
    <MaterialBadge
      anchorOrigin={anchorOrigin}
      badgeContent={badgeContent}
      classes={{
        badge: clsx(
          size === 'SMALL' && classes.badgeSmall,
          size === 'MEDIUM' && classes.badgeMedium,
          size === 'LARGE' && classes.badgeLarge,
          status === 'ERROR' && classes.error,
          status === 'WARNING' && classes.warning,
          status === 'INFO' && classes.info,
          status === 'SUCCESS' && classes.success,
          badgeClasses
        ),
        ...customClasses,
      }}
      invisible={invisible}
      overlap={overlap}
      {...otherProps}
    >
      {children}
    </MaterialBadge>
  );
};

export default Badge;
